import { useState, useEffect } from 'react';
import { Producto, DashboardStats, ConsumoPersonal, ConfiguracionConsumo } from '../types';
import { googleSheetsService } from '../services/googleSheets';
import { localDbService } from '../services/localDatabase';
import { apiService } from '../services/api';
import { isBefore, addDays } from 'date-fns';

export const useInventory = () => {
  const [productos, setProductos] = useState<Producto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const [consumosPersonal, setConsumosPersonal] = useState<ConsumoPersonal[]>([]);
  const [useMockData, setUseMockData] = useState(false); // Use API by default
  const [useLocalDb] = useState(false); // Use API instead of local database
  const [useApi] = useState(true); // Use API by default
  const [configuracionConsumo] = useState<ConfiguracionConsumo>({
    limiteDiario: 1,
    bebidasPermitidas: ['Americano', 'Té', 'Latte', 'Cappuccino'],
    restriccionPorTipo: true,
    tiposPermitidos: ['basico', 'especialidad']
  });

  const fetchProductos = async () => {
    try {
      setLoading(true);
      let data: Producto[] = [];
      
      if (useApi) {
        // Only load from API (MongoDB) - no fallback
        const isApiAvailable = await apiService.healthCheck();
        if (isApiAvailable) {
          data = await apiService.getAllInventory();
          console.log(`Cargados ${data.length} productos desde MongoDB`);
        } else {
          throw new Error('No se puede conectar con MongoDB. Por favor, verifique la conexión.');
        }
      } else if (useLocalDb) {
        // This option should not be used in production
        throw new Error('El modo de base de datos local está deshabilitado. Use MongoDB.');
      } else {
        // Usar Google Sheets o mock data
        data = useMockData 
          ? await googleSheetsService.getMockData()
          : await googleSheetsService.fetchProductsFromSheet();
      }
      
      setProductos(data);
      setLastSync(new Date());
      setError(null);
    } catch (err: any) {
      const errorMessage = err.message || 'Error al cargar datos del inventario';
      setError(errorMessage);
      console.error('Error loading inventory:', err);
      // Do not fallback to mock data - keep products empty
      setProductos([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleDataSource = () => {
    setUseMockData(!useMockData);
  };

  const calculateStats = (): DashboardStats => {
    const today = new Date();
    const proximoAVencer = addDays(today, 7);

    let productosStockBajo = 0;
    let productosVencidos = 0;
    let productosProximosAVencer = 0;

    productos.forEach(producto => {
      if (producto.stockFinal <= producto.stockMinimo) {
        productosStockBajo++;
      }

      const fechaCaducidad = new Date(producto.fechaCaducidad);
      if (isBefore(fechaCaducidad, today)) {
        productosVencidos++;
      } else if (isBefore(fechaCaducidad, proximoAVencer)) {
        productosProximosAVencer++;
      }
    });

    return {
      totalProductos: productos.length,
      productosStockBajo,
      productosVencidos,
      productosProximosAVencer,
      valorInventario: productos.reduce((total, p) => total + (p.stockFinal * 100), 0) // Precio estimado
    };
  };

  const updateProduct = async (producto: Producto) => {
    try {
      const rowIndex = productos.findIndex(p => p.id === producto.id);
      if (rowIndex === -1) {
        console.error('Producto no encontrado:', producto.id, producto.producto);
        return false;
      }

      let success = false;
      
      if (useApi) {
        try {
          console.log('Actualizando producto:', producto.id, producto.producto, 'Stock:', producto.stockFinal);
          success = await apiService.updateProduct(producto.id, producto);
          
          if (success) {
            console.log('Producto actualizado exitosamente:', producto.producto);
            
            // Refresh the product data from the backend to ensure we have the latest
            try {
              const updatedProduct = await apiService.getProductById(producto.id);
              if (updatedProduct) {
                // Use the fresh data from backend
                const updatedProductos = [...productos];
                updatedProductos[rowIndex] = updatedProduct;
                setProductos(updatedProductos);
                console.log('Producto actualizado con datos frescos del servidor:', updatedProduct.stockFinal);
              } else {
                // Fallback to using the passed product if fetch fails
                const updatedProductos = [...productos];
                updatedProductos[rowIndex] = producto;
                setProductos(updatedProductos);
              }
            } catch (fetchError) {
              console.error('Error obteniendo producto actualizado:', fetchError);
              // Still update local state with what we sent
              const updatedProductos = [...productos];
              updatedProductos[rowIndex] = producto;
              setProductos(updatedProductos);
            }
            
            // Check alerts
            try {
              await apiService.checkAndGenerateAlerts(producto.id);
            } catch (alertError) {
              console.error('Error generando alertas:', alertError);
            }
          } else {
            console.error('La API retornó false para:', producto.producto);
          }
        } catch (apiError) {
          console.error('Error actualizando en API/MongoDB para', producto.producto, ':', apiError);
          success = await localDbService.updateProduct(producto.id, producto);
        }
      } else if (useLocalDb) {
        success = await localDbService.updateProduct(producto.id, producto);
        if (success) {
          const updatedProductos = [...productos];
          updatedProductos[rowIndex] = producto;
          setProductos(updatedProductos);
        }
      } else {
        success = await googleSheetsService.updateProductInSheet(producto, rowIndex);
        if (success) {
          const updatedProductos = [...productos];
          updatedProductos[rowIndex] = producto;
          setProductos(updatedProductos);
        }
      }
      
      if (success) {
        setLastSync(new Date());
      }
      return success;
    } catch (err) {
      console.error('Error updating product:', err);
      return false;
    }
  };

  const syncWithGoogleSheets = async () => {
    await fetchProductos();
  };

  const registrarConsumoPersonal = (consumo: Omit<ConsumoPersonal, 'id'>) => {
    const nuevoConsumo: ConsumoPersonal = {
      ...consumo,
      id: Date.now().toString()
    };
    setConsumosPersonal([...consumosPersonal, nuevoConsumo]);
    
    // Actualizar el inventario restando el consumo
    // Aquí podrías conectar con el producto específico si lo necesitas
  };

  useEffect(() => {
    // Only fetch from MongoDB - no localStorage initialization
    fetchProductos();
    
    // Cargar consumos del localStorage si existen (estos sí pueden ser locales por ahora)
    const savedConsumos = localStorage.getItem('consumosPersonal');
    if (savedConsumos) {
      setConsumosPersonal(JSON.parse(savedConsumos));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useMockData, useLocalDb, useApi]); // Re-fetch when data source changes

  useEffect(() => {
    // Guardar consumos en localStorage
    if (consumosPersonal.length > 0) {
      localStorage.setItem('consumosPersonal', JSON.stringify(consumosPersonal));
    }
  }, [consumosPersonal]);

  return {
    productos,
    loading,
    error,
    lastSync,
    stats: calculateStats(),
    updateProduct,
    syncWithGoogleSheets,
    refetch: fetchProductos,
    consumosPersonal,
    configuracionConsumo,
    registrarConsumoPersonal,
    useMockData,
    toggleDataSource
  };
};